
import { Delete, Draw, Hexagon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNovel } from '@/context/novel-context';
import type { MapAreaStyle, MapEntityArea } from '../canvas/types';
import { WorldEntityPicker } from './WorldEntityPicker';
import type { MapEntityRef } from '../canvas/types';
import { sliderValue } from '../canvas/slider-utils';

interface WorldAreasListProps {
    areas: MapEntityArea[];
    isDrawingArea: boolean;
    onStartDrawing: () => void;
    onStopDrawing: () => void;
    onDelete: (uid: string) => void;
    onUpdateStyle: (uid: string, style: Partial<MapAreaStyle>) => void;
    onChangeEntity: (uid: string, ref: MapEntityRef) => void;
    selectedUid: string | null;
    onSelectUid: (uid: string | null) => void;
    currentLocationId?: string;
}

const PRESET_COLORS = [
    '#4a90d9', '#e74c3c', '#2ecc71', '#f39c12',
    '#9b59b6', '#1abc9c', '#e67e22', '#3498db',
];

function ColorSwatch({ color, selected, onClick }: { color: string; selected: boolean; onClick: () => void }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                width: 20, height: 20, borderRadius: '50%', bgcolor: color, cursor: 'pointer',
                border: selected ? '2px solid white' : '2px solid transparent',
                boxShadow: selected ? '0 0 0 2px ' + color : 'none',
                transition: 'all 0.15s',
            }}
        />
    );
}

function AreaStyleControls({ area, onUpdate }: { area: MapEntityArea; onUpdate: (s: Partial<MapAreaStyle>) => void }) {
    const { t } = useTranslation();
    const { style } = area;
    return (
        <Stack spacing={1.5} mt={1} pl={0.5} onClick={e => e.stopPropagation()}>
            <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('map_creator.world_areas.style.fill_color')}
                </Typography>
                <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" gap={0.5}>
                    {PRESET_COLORS.map(c => (
                        <ColorSwatch key={c} color={c} selected={style.fillColor === c} onClick={() => onUpdate({ fillColor: c })} />
                    ))}
                    <Box
                        component="input"
                        type="color"
                        value={style.fillColor}
                        onChange={e => onUpdate({ fillColor: e.target.value })}
                        title={t('map_creator.world_areas.style.custom_fill_color')}
                        sx={{ width: 20, height: 20, border: 'none', borderRadius: '50%', p: 0, cursor: 'pointer', bgcolor: 'transparent' }}
                    />
                </Stack>
            </Box>

            <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('map_creator.world_areas.style.fill_opacity', { value: Math.round(style.fillOpacity * 100) })}
                </Typography>
                <Slider
                    size="small"
                    min={0} max={1} step={0.05}
                    value={style.fillOpacity}
                    onChange={(_, v) => onUpdate({ fillOpacity: sliderValue(v) })}
                />
            </Box>

            <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('map_creator.world_areas.style.border_color')}
                </Typography>
                <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" gap={0.5}>
                    {PRESET_COLORS.map(c => (
                        <ColorSwatch key={c} color={c} selected={style.borderColor === c} onClick={() => onUpdate({ borderColor: c })} />
                    ))}
                    <Box
                        component="input"
                        type="color"
                        value={style.borderColor}
                        onChange={e => onUpdate({ borderColor: e.target.value })}
                        title={t('map_creator.world_areas.style.custom_border_color')}
                        sx={{ width: 20, height: 20, border: 'none', borderRadius: '50%', p: 0, cursor: 'pointer' }}
                    />
                </Stack>
            </Box>

            <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('map_creator.world_areas.style.border_width', { value: style.borderWidth })}
                </Typography>
                <Slider
                    size="small"
                    min={0} max={8} step={0.5}
                    value={style.borderWidth}
                    onChange={(_, v) => onUpdate({ borderWidth: sliderValue(v) })}
                />
            </Box>

            <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('map_creator.world_areas.style.border_style')}
                </Typography>
                <Stack direction="row" spacing={0.5} mt={0.5}>
                    {[
                        { label: t('map_creator.world_areas.style.presets.solid'), value: [] },
                        { label: t('map_creator.world_areas.style.presets.dashed'), value: [8, 4] },
                        { label: t('map_creator.world_areas.style.presets.dotted'), value: [2, 4] },
                    ].map(opt => (
                        <Chip
                            key={opt.label}
                            label={opt.label}
                            size="small"
                            onClick={() => onUpdate({ dashPattern: opt.value })}
                            variant={JSON.stringify(style.dashPattern) === JSON.stringify(opt.value) ? 'filled' : 'outlined'}
                            sx={{ fontSize: '0.65rem' }}
                        />
                    ))}
                </Stack>
            </Box>
        </Stack>
    );
}

export function WorldAreasList({
    areas, isDrawingArea, onStartDrawing, onStopDrawing,
    onDelete, onUpdateStyle, onChangeEntity,
    selectedUid, onSelectUid, currentLocationId,
}: WorldAreasListProps) {
    const { t } = useTranslation();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [relinkUid, setRelinkUid] = useState<string | null>(null);

    const { novel } = useNovel();
    const locationSummaries = novel?.summaries?.locations || {};
    const eventSummaries = novel?.summaries?.events || {};

    const getEntityName = (ref: { type: 'locations' | 'events', id: string, name: string }) => {
        if (ref.type === 'locations') {
            const sum = locationSummaries[ref.id];
            if (sum) return sum.name;
        } else if (ref.type === 'events') {
            const sum = eventSummaries[ref.id];
            if (sum) return sum.name;
        }
        return ref.name;
    };

    const handleRelink = (uid: string) => {
        setRelinkUid(uid);
        setPickerOpen(true);
    };

    const handlePickerSelect = (ref: MapEntityRef) => {
        if (relinkUid) {
            onChangeEntity(relinkUid, ref);
            setRelinkUid(null);
        }
        setPickerOpen(false);
    };

    return (
        <Stack spacing={1.5}>
            <Button
                variant={isDrawingArea ? 'contained' : 'outlined'}
                color={isDrawingArea ? 'warning' : 'primary'}
                startIcon={<Draw />}
                fullWidth
                size="small"
                onClick={isDrawingArea ? onStopDrawing : onStartDrawing}
            >
                {isDrawingArea 
                    ? t('map_creator.world_areas.drawing') 
                    : t('map_creator.world_areas.draw_button')}
            </Button>

            {isDrawingArea && (
                <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                    <Typography variant="caption" fontWeight="bold">
                        {t('map_creator.world_areas.draw_instructions')}
                    </Typography>
                </Box>
            )}

            <Divider />

            <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', color: 'text.secondary' }}>
                {t('map_creator.world_areas.placed_title', { count: areas.length })}
            </Typography>

            {areas.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                    {t('map_creator.world_areas.empty_areas')}
                </Typography>
            )}

            <List dense disablePadding>
                {areas.map(area => {
                    const isSelected = selectedUid === area.uid;
                    return (
                        <ListItem
                            key={area.uid}
                            disablePadding
                            sx={{
                                mb: 0.5, borderRadius: 1, border: '1px solid',
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                bgcolor: isSelected ? 'action.selected' : 'background.paper',
                                cursor: 'pointer', flexDirection: 'column', alignItems: 'stretch', p: 1,
                            }}
                            onClick={() => onSelectUid(isSelected ? null : area.uid)}
                        >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: area.style.fillColor, border: `2px solid ${area.style.borderColor}`, flexShrink: 0 }} />
                                <ListItemText
                                    primary={getEntityName(area.entityRef)}
                                    secondary={t('map_creator.world_areas.vertices_count', { count: area.vertices.length })}
                                    primaryTypographyProps={{ variant: 'body2', fontSize: '0.75rem', fontWeight: 'bold' }}
                                    secondaryTypographyProps={{ variant: 'caption', fontSize: '0.65rem' }}
                                    sx={{ flex: 1, m: 0 }}
                                />
                                <Tooltip title={t('map_creator.world_areas.tooltips.relink')}>
                                    <IconButton size="small" onClick={e => { e.stopPropagation(); handleRelink(area.uid); }}>
                                        <Hexagon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('map_creator.world_areas.tooltips.delete')}>
                                    <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); onDelete(area.uid); }}>
                                        <Delete sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                            {isSelected && (
                                <AreaStyleControls area={area} onUpdate={s => onUpdateStyle(area.uid, s)} />
                            )}
                        </ListItem>
                    );
                })}
            </List>

            <WorldEntityPicker
                open={pickerOpen}
                onClose={() => { setPickerOpen(false); setRelinkUid(null); }}
                onSelect={handlePickerSelect}
                locationsOnly
                currentLocationId={currentLocationId}
            />
        </Stack>
    );
}
